<template>
  <VContainer
    id="container"
    v-update-container-height
    fluid
    ma-0
    pa-0
  >
    <VLayout
      column
      justify-end
      fill-height
    >
      <VFlex
        xs12
      >
        <VTabs
          centered
          height="70"
          color="grey lighten-4"
          icons-and-text
          slider-color="blue"
          @change="onTabChanged"
        >
          <VTab
            v-for="i in Object.keys(tabs).length"
            :key="i"
            :href="'#tab-' + i"
          >
            {{ getTabName('tab-' + i) }} ({{ getTotalContacts('tab-' + i) }})
          </VTab>
          <VTabItem
            v-for="i in Object.keys(tabs).length"
            :key="i"
            :value="'tab-' + i"
          >
            <VCard
              flat
              :height="window.height"
              style="overflow: auto"
            >
              <ListViewPage
                v-if="currentTab === 'tab-' + i"
                :get-gql-query="getGqlQuery"
                :update-gql-query="updateGqlQuery"
                :get-current-table-data="getCurrentTableData"
                :get-query-variables="getQueryVariables"
                :update-query-variables="updateQueryVariables"
                :running-update-query="runningUpdateQuery"
                :update-query-disabled="updateQueryDisabled"
                :update-context-variable="updateContextVariable"
                :fetching-more="isFetchingMore"
                :get-loading-status="getLoadingStatus"
                :set-loading-status="setLoadingStatus"
                :get-status-message="getStatusMessage"
                :set-status-message="setStatusMessage"
                :parse-get-data="parseGetData"
                :parse-update-data="parseUpdateData"
                :clear-data="clearData"
              >
                <template slot="table">
                  <ContactTable
                    :selectable="contactsSelectable"
                    :select-all="selectAll"
                    :archive-contact="!contactsSelectable && archiveContact"
                    :swipe-contact="swipeContact"
                    :selected-ids.sync="selectedIds"
                    :unselected-ids.sync="unselectedIds"
                    @fetchingMore="onFetchingMore"
                  />
                </template>
              </ListViewPage>
            </VCard>
          </VTabItem>
        </VTabs>
      </VFlex>
      <VFlex
        id="messageBox"
        xs12
      >
        <Transition name="fade">
          <MassTextBox
            v-if="contactsSelectable"
            v-update-message-box-height
            :get-gql-query="getGqlQuery"
            :get-query-variables="getQueryVariables"
            :selected-ids="selectedIds"
            :unselected-ids="unselectedIds"
            :select-all.sync="selectAll"
          />
        </Transition>
      </VFlex>
    </VLayout>
  </VContainer>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { contactMapGetters, contactMapActions } from '@/store/utils'
import ListViewPage from '@/pages/ListViewPage'
import ContactTable from '@/components/ContactTable'
import CONTACTS_WITH_WORK_QUERY from '@/backend/graphql/contact/ContactsWithWork.gql'
import ACTIVE_CONTACTS_BY_NUMBER from '@/backend/graphql/contact/ActiveContactsByNumber.gql'
import MassTextBox from '@/components/MassTextBox'
import _ from 'lodash'

export default {
  name: 'BaseListContactsPage',
  components: { ContactTable, ListViewPage, MassTextBox },
  directives: {
    // TODO: modify 93 px
    'update-message-box-height': {
      inserted: function (el, binding, vnode) {
        const self = vnode.context
        const footerHeight = document.getElementById('messageBox').clientHeight
        const containerHeight = self.window.initialHeight > 0
          ? self.window.initialHeight : document.getElementById('container').parentElement.parentElement.clientHeight
        const windowHeight = containerHeight - footerHeight - 93
        self.window.height = windowHeight
      },
      'unbind': function (el, binding, vnode) {
        const self = vnode.context
        const containerHeight = self.window.initialHeight > 0
          ? self.window.initialHeight : document.getElementById('container').parentElement.parentElement.clientHeight
        const windowHeight = containerHeight - 93
        self.window.height = windowHeight
      }
    },
    'update-container-height': {
      inserted: function (el, binding, vnode) {
        const self = vnode.context
        self.window.initialHeight = el.parentElement.parentElement.clientHeight
        if (!self.contactsSelectable) {
          self.window.height = self.window.initialHeight - 93
        }
      }
    }
  },
  props: {
    tabs: {
      type: Object,
      default: function () {
        return {}
      }
    },
    currentTab: {
      type: String,
      default: 'tab-1'
    },
    contactsSelectable: {
      type: Boolean,
      default: false
    },
    getGqlQuery: {
      type: Object,
      default: function () {
        return CONTACTS_WITH_WORK_QUERY
      }
    },
    updateGqlQuery: {
      type: Object,
      default: function () {
        return ACTIVE_CONTACTS_BY_NUMBER
      }
    },
    getItems: {
      type: Function,
      default: function (data) {
        return data.listContacts.results
      }
    },
    getCurrentTableData: {
      type: Function,
      default: function () {
        return this.$store.getters['contact/getAllContacts']
      }
    },
    getTotalItemsCount: {
      type: Function,
      default: function (data) {
        return data.listContacts.totalCount
      }
    },
    getQueryVariables: {
      type: Function,
      default: function () {
        return {}
      }
    },
    updateQueryVariables: {
      type: Function,
      default: function () {
        return null
      }
    },
    runningUpdateQuery: {
      type: Boolean,
      default: false
    },
    updateQueryDisabled: {
      type: Boolean,
      default: true
    },
    updateContextVariable: {
      type: Function,
      default: function (key, value) {
        if (this.hasOwnProperty(key)) {
          this[key] = value
        }
      }
    },
    onTabChanged: {
      type: Function,
      default: function (value) {}
    },
    getTotalContacts: {
      type: Function,
      default: function (value) {
        return 0
      }
    },
    archiveContact: {
      type: Boolean,
      default: false
    },
    swipeContact: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      isFetchingMore: false,
      window: {
        width: 0,
        height: 0,
        initialHeight: 0
      },
      selectAll: false,
      selectedIds: new Set(),
      unselectedIds: new Set()
    }
  },
  computed: {
    ...mapGetters(Object.assign(contactMapGetters))
  },
  created () {
    window.addEventListener('resize', this.handleResize)
    this.handleResize()
    this.setStatusMessage('Loading Contacts. Please Wait...')
    this.$store.dispatch('messageDrawer/reset')
    this.setMessageDrawerVisibility({ val: true })
    this.$emit('pageCreated')
  },
  destroyed () {
    window.removeEventListener('resize', this.handleResize)
    this.$emit('pageDestroyed')
  },
  methods: {
    ...mapActions(Object.assign(contactMapActions, {
      setMessageDrawerVisibility: 'messageDrawer/setVisibility'
    })),
    getLoadingStatus () { return this.$store.getters['contact/getLoadingStatus'] },
    setLoadingStatus (val) { this.$store.dispatch('contact/setLoadingStatus', { val }) },
    getStatusMessage () { return this.$store.getters['contact/getLoadingText'] },
    setStatusMessage (message) { this.$store.dispatch('contact/setStatusMessage', { message }) },
    parseGetData (data) {
      if (this.allContacts.length === 0) {
        this.initContacts({
          newContacts: this.getItems(data),
          totalCount: this.getTotalItemsCount(data),
          isFullArraySize: false
        })
      } else {
        this.appendContacts({
          newContacts: this.getItems(data),
          startIndex: this.getQueryVariables().offset,
          setIndex: this.getQueryVariables().offset,
          updateMessage: false,
          selectAll: this.selectAll
        })
        this.isFetchingMore = false
      }
    },
    parseUpdateData (data) {
      this.prependContacts({
        newContacts: this.getItems(data),
        checkInbox: true
      })
    },
    clearData () {
      this.clearContacts()
    },
    onFetchingMore () {
      if (!this.isFetchingMore) {
        this.isFetchingMore = true
      }
    },
    getTabName (value) {
      return _.get(this.tabs, value, '')
    },
    handleResize () {
      this.window.width = window.innerWidth
      const mainElement = document.getElementById('container')
      if (mainElement) {
        // 64 is toolbar title
        this.window.initialHeight = window.innerHeight - 64
        const footerHeight = document.getElementById('messageBox').clientHeight
        const containerHeight = this.window.initialHeight
        const windowHeight = containerHeight - footerHeight - 93
        this.window.height = windowHeight
      }
    }
  }
}
</script>

<style scoped>
  .text-xs-center{
    margin-top: 40px;
  }
  .fade-enter-active, .fade-leave-active {
    transition: opacity .5s;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
</style>
